// apps/client/src/world/terrain/ramps.ts — a grassy wedge on every ramp tile, rising one tier along its ascent direction (D-06). It
// wears the grass and clay of the terrace it climbs onto, and its top is lit nearly like the flat caps (M1 playtest: the true 45°
// normals turned every ramp into a flat bright slab facing the sun, in the lowest terrace's colours).
import { tuning } from '@whiskerton/content';
import { rampDirAt, tierAt, type MapData } from '@whiskerton/shared';
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { terraceMaterials } from './terraces';

const LIP = 0.16;      // the top runs this far onto the upper tier, over the terrace's rolled edge
const SINK = 0.6;      // the clay sides reach this far below the low end, so no gap shows on a sloping beach
/** How far the top's normals lean from straight up toward the slope's own (0 = lit exactly like a flat cap, 1 = the true slope). A
 *  little lean keeps a hint of the climb without the sun-facing glare. */
const SLOPE_SHADE = 0.3;

type V = [number, number, number];
type Quad = { pts: readonly V[]; uv: (p: V) => [number, number] };
/** Flat-shaded quads; `lean` (0…1) bends every normal from straight up toward the face's own one. */
function quads(list: readonly Quad[], lean = 1): THREE.BufferGeometry {
  const pos: number[] = [], uv: number[] = [];
  for (const { pts, uv: f } of list) for (const k of [0, 1, 2, 0, 2, 3]) { pos.push(...pts[k]!); uv.push(...f(pts[k]!)); }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3));
  geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(uv), 2));
  geo.computeVertexNormals();
  if (lean < 1) {
    const n = geo.getAttribute('normal') as THREE.BufferAttribute, v = new THREE.Vector3();
    for (let i = 0; i < n.count; i++) {
      v.set(n.getX(i) * lean, n.getY(i) * lean + (1 - lean), n.getZ(i) * lean).normalize();
      n.setXYZ(i, v.x, v.y, v.z);
    }
  }
  return geo;
}

export function buildRamps(map: Pick<MapData, 'w' | 'h' | 'tiles'>): THREE.Mesh {
  const rise = tuning.map.tierRise;
  // Per terrace k = 1…3 (the tier a ramp climbs onto): its grass tops and clay sides.
  const byK = ([1, 2, 3] as const).map(() => ({ tops: [] as Quad[], sides: [] as Quad[] }));
  const worldUv = (p: V): [number, number] => [p[0], -p[2]];          // the terrace caps' UVs, so the meadow lines up
  for (let y = 0; y < map.h; y++) for (let x = 0; x < map.w; x++) {
    const dir = rampDirAt(map, x, y);
    if (dir === null) continue;
    const tier = tierAt(map, x, y), lo = tier * rise, hi = lo + rise, base = lo - SINK;
    const { tops, sides } = byK[Math.min(2, Math.max(0, tier))]!;
    // Work in a frame where the ramp climbs along +u: u ∈ [0, 1 + LIP], v ∈ [0, 1]; map (u, v) to world (x, z) by a quarter-turn
    // rotation of the east-climbing frame (never a mirror, so every face keeps its winding).
    const at = (u: number, v: number, h: number): V => {
      switch (dir) {
        case 0: return [x + 1 - v, h, y + 1 - u];   // climbs north (−z)
        case 1: return [x + u, h, y + 1 - v];       // climbs east
        case 2: return [x + v, h, y + u];           // climbs south (+z)
        default: return [x + 1 - u, h, y + v];      // climbs west
      }
    };
    const lip = hi + 0.004;
    tops.push({ pts: [at(0, 0, lo), at(1, 0, hi), at(1, 1, hi), at(0, 1, lo)], uv: worldUv });
    tops.push({ pts: [at(1, 0, lip), at(1 + LIP, 0, lip), at(1 + LIP, 1, lip), at(1, 1, lip)], uv: worldUv });
    for (const v of [0, 1]) {                                            // the two clay sides, as a slope quad and a lip quad
      const ord = (p: V[]) => (v === 0 ? p : [p[1]!, p[0]!, p[3]!, p[2]!]);
      sides.push({ pts: ord([at(0, v, base), at(1, v, base), at(1, v, hi), at(0, v, lo)]), uv: worldUv });
      sides.push({ pts: ord([at(1, v, base), at(1 + LIP, v, base), at(1 + LIP, v, lip), at(1, v, lip)]), uv: worldUv });
    }
  }
  // Groups in material order [grass1, clay1, grass2, clay2, grass3, clay3]; a terrace without ramps gets empty groups.
  const parts = byK.flatMap(({ tops, sides }) => [quads(tops, SLOPE_SHADE), quads(sides)]);
  const geo = byK.some((b) => b.tops.length) ? mergeGeometries(parts, true) : new THREE.BufferGeometry();
  for (const p of parts) p.dispose();
  const mesh = new THREE.Mesh(geo, ([1, 2, 3] as const).flatMap((k) => terraceMaterials(k)));
  mesh.name = 'ramps';
  mesh.castShadow = mesh.receiveShadow = true;
  return mesh;
}
